import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_STATE } from "../../constants/api";
import { ReportsMenuStructure } from "../../menuStructures";
import { Table, Column, Cell } from "rsuite-table";
import "rsuite-table/dist/css/rsuite-table.css";
import "../../../css/rstable-overwrites.scss";
import { Pagination, ReportControls } from "../../components/reports";
import {
  CurrencyRenderer,
  TextInputFilter,
} from "../../components/reports/CellTypes";
import { useFetchCompletedBuylistReport } from "../../hooks/reports/useFetchCompletedBuylistReport";
import { useStoreDetails } from "../../hooks/storeHooks";
import { useShowError } from "../../hooks/errorHooks";
import { useReportsDetails } from "../../hooks/reportsHooks";
import SetActiveMenu from "../../menuStructures/SetActiveMenu";
import { rowPerPageOptions } from "../../constants/reports";
import { addMultipleLocalizedDates } from "../../transformers/helpers/addLocalizedDate";
import { completedBuylistCSVHeaders } from "../../constants/reportCSVHeaders";
import CustomHeaderCell from "../../components/reports/CustomHeader";

function PaymentTypeSelect({ value, setValue }) {
  return (
    <select
      onChange={(event) => setValue(event.target.value)}
      value={value}
      aria-label="Filter by payment type"
    >
      <option value="">All</option>
      <option value="Cash">Cash</option>
      <option value="Credit">Store Credit</option>
    </select>
  );
}

function CompletedBuylist() {
  const {
    apiState,
    currentPage,
    filterValues,
    overrideTableHeight,
    rowsPerPage,
    sortColumn,
    tableData,
    computedTableData,
    reset,
    setApiState,
    setCurrentPage,
    setTableRowsPerPage,
    setFilters,
    setSortingColumn,
    setSourceDataDetails,
    updateFilterValue,
  } = useReportsDetails();
  const { currencyCode } = useStoreDetails();
  const showError = useShowError();
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [timeZone, setTimeZone] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  const {
    data: sourceData,
    isFetching,
    error,
  } = useFetchCompletedBuylistReport(
    {
      startDate,
      endDate,
    },
    {
      enabled: !!startDate && !!endDate,
    }
  );

  useEffect(() => {
    document.title = "Reports | BinderPOS";
    reset();
    setFilters(filters);
  }, []);

  useEffect(() => {
    if (isFetching) {
      setApiState(API_STATE.LOADING);
    } else if (error) {
      showError(
        error as any,
        "Failed to load report",
        "There was an error retrieving your buylist report. Please refresh and try again"
      );
      setApiState(API_STATE.ERROR);
    } else if (sourceData) {
      setSourceDataDetails(sourceData);
      setApiState(API_STATE.SUCCESS);
    }
  }, [isFetching, sourceData, error]);

  const filters = [
    {
      name: "buylist Id",
      condition: (filterValues) => filterValues?.buylistId?.length,
      filterFunction: (buylistDetail, filterValues) =>
        buylistDetail.buylistId &&
        String(buylistDetail.buylistId).includes(filterValues?.buylistId),
    },
    {
      name: "customer name",
      condition: (filterValues) => filterValues?.customerName?.length,
      filterFunction: (buylistDetail, filterValues) =>
        (buylistDetail.firstName &&
          buylistDetail.firstName
            .toLowerCase()
            .includes(filterValues?.customerName.toLowerCase())) ||
        (buylistDetail.lastName &&
          buylistDetail.lastName
            .toLowerCase()
            .includes(filterValues?.customerName.toLowerCase())),
    },
    {
      name: "customer email",
      condition: (filterValues) => filterValues?.customerEmail?.length,
      filterFunction: (buylistDetail, filterValues) =>
        buylistDetail.email &&
        buylistDetail.email
          .toLowerCase()
          .includes(filterValues?.customerEmail.toLowerCase()),
    },
    {
      name: "payment type",
      condition: (filterValues) => filterValues?.paymentType?.length,
      filterFunction: (buylistDetail, filterValues) =>
        buylistDetail.paymentType === filterValues?.paymentType,
    },
    {
      name: "status",
      condition: (filterValues) => filterValues?.status?.length,
      filterFunction: (buylistDetail, filterValues) =>
        buylistDetail.status === filterValues?.status,
    },
  ];

  const handleSetDateRange = (fromDate, toDate, tz) => {
    setStartDate(fromDate);
    setEndDate(toDate);
    setTimeZone(tz);
  };

  const {
    buylistId = "",
    customerName = "",
    customerEmail = "",
    paymentType = "",
  } = filterValues;

  const csvFilename = "Completed Buylist Report.csv";

  const data = addMultipleLocalizedDates(computedTableData(), timeZone, [
    "dateCompleted",
    "dateSubmitted",
  ]);

  return (
    <Fragment>
      <SetActiveMenu menuStructure={ReportsMenuStructure} />
      <div className="app-header">
        <h1 className="title">
          <span className="header-text">Completed Buylist Report</span>
        </h1>
      </div>
      <div className="report">
        <ReportControls
          setDateRange={handleSetDateRange}
          csvHeaders={completedBuylistCSVHeaders}
          csvFilename={csvFilename}
        />
        {apiState !== API_STATE.INITIAL && (
          <div className="data-table">
            <div className="table-container">
              <Table
                data={data}
                headerHeight={80}
                sortColumn={sortColumn.key}
                sortType={sortColumn.order}
                onSortColumn={setSortingColumn}
                renderEmpty={() => <div>No data to display</div>}
                loading={apiState == API_STATE.LOADING}
                className="rs-table--multi-header"
                fillHeight={overrideTableHeight === 0}
              >
                <Column key="buylistId" width={100} sortable resizable>
                  <CustomHeaderCell title="Buylist #">
                    <TextInputFilter
                      title="Buylist Id"
                      aria-label="Filter by buylist ID"
                      value={buylistId}
                      setValue={(value) =>
                        updateFilterValue("buylistId", value)
                      }
                    />
                  </CustomHeaderCell>
                  <Cell dataKey="buylistId">
                    {(rowData) => (
                      <Link
                        to={`/buylists/pending/moreDetail/${rowData.buylistId}`}
                        className="viewBuylist"
                      >
                        {rowData.buylistId}
                      </Link>
                    )}
                  </Cell>
                </Column>
                <Column key="customerName" width={200} sortable resizable>
                  <CustomHeaderCell title="Customer Name">
                    <TextInputFilter
                      title="Customer name"
                      aria-label="Filter by customer name"
                      value={customerName}
                      setValue={(value) =>
                        updateFilterValue("customerName", value)
                      }
                    />
                  </CustomHeaderCell>
                  <Cell dataKey="customerName" />
                </Column>
                <Column
                  key="col_customerEmail"
                  width={200}
                  align="left"
                  sortable
                  resizable
                >
                  <CustomHeaderCell title="Customer Email">
                    <TextInputFilter
                      title="Customer Email"
                      aria-label="Filter by customer email"
                      value={customerEmail}
                      setValue={(value) =>
                        updateFilterValue("customerEmail", value)
                      }
                    />
                  </CustomHeaderCell>
                  <Cell dataKey="email" />
                </Column>
                <Column key="col_paymentType" width={150} sortable resizable>
                  <CustomHeaderCell title="Payment Type">
                    <PaymentTypeSelect
                      value={paymentType}
                      setValue={(value) =>
                        updateFilterValue("paymentType", value)
                      }
                    />
                  </CustomHeaderCell>
                  <Cell dataKey="paymentType" />
                </Column>
                <Column key="col_totalItems" width={80} sortable resizable>
                  <CustomHeaderCell title="Total Items" />
                  <Cell dataKey="totalItems" />
                </Column>
                <Column key="col_totalBuyPrice" width={120} sortable resizable>
                  <CustomHeaderCell title="Total Buy Price" />
                  <Cell dataKey="totalBuyPrice">
                    {(rowData) =>
                      CurrencyRenderer({
                        cellData: rowData.totalBuyPrice,
                        currency: currencyCode,
                      })
                    }
                  </Cell>
                </Column>
                <Column key="col_dateSubmitted" width={150} sortable resizable>
                  <CustomHeaderCell title="Date Submitted" />
                  <Cell dataKey="dateSubmitted">
                    {(rowData) => rowData.dateSubmitted_localized}
                  </Cell>
                </Column>
                <Column key="col_dateCompleted" width={150} sortable resizable>
                  <CustomHeaderCell title="Date Completed" />
                  <Cell dataKey="dateCompleted">
                    {(rowData) => rowData.dateCompleted_localized}
                  </Cell>
                </Column>
              </Table>
            </div>
            <Pagination
              apiState={apiState}
              currentPage={currentPage}
              totalRowCount={tableData.length}
              rowsPerPage={rowsPerPage}
              rowPerPageOptions={rowPerPageOptions}
              setCurrentPage={setCurrentPage}
              setRowsPerPage={setTableRowsPerPage}
            />
          </div>
        )}
      </div>
    </Fragment>
  );
}

export default CompletedBuylist;
